import { useEffect, useState } from 'react';
import { MeetingTopicPicker } from '../components/MeetingTopicPicker';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  appointmentPatchSchema,
  type AppointmentPatch,
  type AppointmentPatchInput,
} from '@nadmai/shared';
import { Layout, LoadingScreen, ErrorNotice } from '../components/Layout';
import { api } from '../lib/api';
import type { AiFieldSuggestion, Appointment } from '../lib/types';
import { MeetingFilesPanel } from '../components/MeetingFilesPanel';

interface Calendar {
  id: string;
  name: string;
  primary: boolean;
}

interface SuggestedItem {
  title?: string | null;
  appointmentDate?: string | null;
  startTime?: string | null;
}

interface SuggestionPickerState {
  key: string;
  label: string;
  currentValue: string;
  suggestions: AiFieldSuggestion[];
  apply: (value: string) => void;
}

function Confidence({ value }: { value?: number }) {
  if (value === undefined || value >= 0.65) return null;
  return (
    <span
      className={`ml-2 rounded-full px-2 py-0.5 text-xs ${value < 0.4 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}
    >
      ควรตรวจสอบ {Math.round(value * 100)}%
    </span>
  );
}

function templateDefaults(
  templateType: 'meeting' | 'task' | 'medical' | 'activity',
): Record<string, unknown> {
  if (templateType === 'meeting')
    return {
      templateType,
      organizer: null,
      participants: [],
      agenda: null,
      meetingLink: null,
      materials: [],
      relatedTaskTitle: null,
      relatedTaskDueDate: null,
      referenceNumber: null,
    };
  if (templateType === 'task')
    return {
      templateType,
      summaryPoints: [],
      assignedBy: null,
      contact: null,
      organization: null,
      location: null,
      referenceNumber: null,
      priority: 'normal',
      relatedMeetingDate: null,
    };
  if (templateType === 'activity')
    return {
      templateType,
      activityType: null,
      dressCode: null,
      golfCourse: null,
      teeOffTime: null,
      golfGroupId: null,
      golfGroupName: null,
      golfers: [],
      price: null,
      bookedBy: null,
      golfStartMode: 'tee_time',
      golfStartHole: null,
    };
  return {
    templateType,
    serviceType: null,
    preparationItems: [],
    organization: null,
    department: null,
    doctorName: null,
    patientName: null,
    location: null,
    referenceNumber: null,
    phone: null,
    instructions: null,
    otherNotes: null,
  };
}

function textValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function isGolfActivity(value: unknown): boolean {
  const normalized = textValue(value).trim().toLocaleLowerCase('th-TH');
  return normalized.includes('golf') || normalized.includes('กอล์ฟ');
}

const multilineListKeys = new Set(['participants', 'materials', 'preparationItems', 'golfers']);

function normalizeTemplateDataForSave(
  templateData: Record<string, unknown>,
): Record<string, unknown> {
  const normalized = { ...templateData };
  for (const key of multilineListKeys) {
    if (typeof normalized[key] === 'string') {
      normalized[key] = normalized[key]
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
    }
  }
  return normalized;
}

export function ReviewPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [meetingTopicTitle, setMeetingTopicTitle] = useState('');
  const [templateData, setTemplateData] = useState<Record<string, unknown>>({});
  const [picker, setPicker] = useState<SuggestionPickerState | null>(null);
  const [customValue, setCustomValue] = useState('');
  const [sourceOpen, setSourceOpen] = useState(false);
  const appointment = useQuery({
    queryKey: ['appointment', id],
    queryFn: () => api<{ data: Appointment }>(`/api/appointments/${id}`).then((r) => r.data),
  });
  const locationHistory = useQuery({
    queryKey: ['meeting-location-suggestions', id],
    queryFn: () =>
      api<{ data: Array<{ value: string; lastUsedAt: string }> }>(
        `/api/appointments/${id}/location-suggestions`,
      ).then((response) => response.data),
    enabled: appointment.data?.templateType === 'meeting',
  });
  const calendars = useQuery({
    queryKey: ['calendars'],
    queryFn: () => api<{ data: Calendar[] }>('/api/calendars').then((response) => response.data),
    enabled: Boolean(appointment.data?.googleCalendarId),
  });
  const activityTypes = useQuery({
    queryKey: ['activity-types'],
    queryFn: () =>
      api<{ data: string[] }>('/api/appointments/activity-types').then((response) => response.data),
    enabled: appointment.data?.templateType === 'activity',
  });
  const golfGroups = useQuery({
    queryKey: ['golf-groups'],
    queryFn: () =>
      api<{ data: Array<{ id: string; name: string; members: string[] }> }>(
        '/api/appointments/golf-groups',
      ).then((response) => response.data),
    enabled: appointment.data?.templateType === 'activity',
  });
  const createSuggestedItem = useMutation({
    mutationFn: (index: number) =>
      api(`/api/appointments/${id}/suggested-items`, {
        method: 'POST',
        body: JSON.stringify({ index }),
      }),
    onSuccess: () => appointment.refetch(),
  });
  const form = useForm<AppointmentPatchInput, unknown, AppointmentPatch>({
    resolver: zodResolver(appointmentPatchSchema),
    defaultValues: {
      templateType: 'medical',
      title: '',
      appointmentDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      timezone: 'Asia/Bangkok',
      organization: null,
      department: null,
      doctorName: null,
      location: null,
      referenceNumber: null,
      phone: null,
      instructions: null,
      patientName: null,
      otherNotes: null,
    },
  });
  const draftKey = `nadmai:draft:${id}`;

  useEffect(() => {
    if (!appointment.data) return;
    const item = appointment.data;
    const type = item.templateType === 'auto' ? 'medical' : item.templateType;
    const golfPreset = sessionStorage.getItem(`nadmai:activity-preset:${id}`) === 'golf';
    const nextTemplateData: Record<string, unknown> = {
      ...templateDefaults(type),
      ...item.templateData,
      templateType: type,
    };
    if (type === 'activity' && golfPreset) {
      nextTemplateData.activityType = 'Golf';
      nextTemplateData.golfStartMode ??= 'tee_time';
      nextTemplateData.golfers ??= [];
    }
    setTemplateData(nextTemplateData);
    const serverValues: AppointmentPatchInput = {
      templateType: item.templateType === 'auto' ? 'medical' : item.templateType,
      title: item.title ?? '',
      appointmentDate: item.appointmentDate ?? '',
      endDate: item.endDate ?? item.appointmentDate ?? '',
      startTime: item.startTime ?? '',
      endTime: item.endTime ?? '',
      timezone: item.timezone,
      organization: item.organization,
      department: item.department,
      doctorName: item.doctorName,
      location: item.location,
      referenceNumber: item.referenceNumber,
      phone: item.phone,
      instructions: item.instructions,
      patientName: item.patientName,
      otherNotes: item.otherNotes,
      expectedUpdatedAt: item.updatedAt,
      templateData: undefined,
    };
    const saved = localStorage.getItem(draftKey);
    if (saved) {
      try {
        form.reset({ ...serverValues, ...(JSON.parse(saved) as Partial<AppointmentPatchInput>) });
        return;
      } catch {
        localStorage.removeItem(draftKey);
      }
    }
    form.reset(serverValues);
  }, [appointment.data, draftKey, form, id]);

  useEffect(() => {
    if (!appointment.data?.groupItems) return;
    setSelection(
      Object.fromEntries(
        appointment.data.groupItems.map((item) => [item.id, item.selectedForCreation]),
      ),
    );
  }, [appointment.data?.groupItems]);

  useEffect(() => {
    const subscription = form.watch((values) => {
      const safeDraft = { ...values, expectedUpdatedAt: appointment.data?.updatedAt };
      localStorage.setItem(draftKey, JSON.stringify(safeDraft));
    });
    return () => subscription.unsubscribe();
  }, [appointment.data?.updatedAt, draftKey, form]);

  if (appointment.isLoading)
    return (
      <Layout title="ตรวจสอบข้อมูล">
        <LoadingScreen />
      </Layout>
    );
  if (!appointment.data)
    return (
      <Layout title="ตรวจสอบข้อมูล">
        <ErrorNotice message="โหลดข้อมูลใบนัดไม่สำเร็จ" />
      </Layout>
    );
  const confidence = appointment.data.ocr?.confidence ?? {};
  const fieldSuggestions = appointment.data.ocr?.extracted.fieldSuggestions ?? {};
  const templateType =
    appointment.data.templateType === 'auto' ? 'medical' : appointment.data.templateType;
  const allDay = !form.watch('startTime') && !form.watch('endTime');
  const templateLabel =
    templateType === 'meeting'
      ? 'นัดประชุม'
      : templateType === 'task'
        ? 'สิ่งที่ต้องทำ'
        : templateType === 'activity'
          ? 'กิจกรรม'
          : 'นัดแพทย์';
  const sourceUrl =
    appointment.data.sourceFileType === 'application/pdf'
      ? `/api/appointments/${id}/file`
      : `/api/appointments/${id}/image`;
  const sourceIsPdf = appointment.data.sourceFileType === 'application/pdf';
  const suggestedItems = Array.isArray(appointment.data.ocr?.providerMetadata.suggestedItems)
    ? appointment.data.ocr.providerMetadata.suggestedItems.filter(
        (item): item is SuggestedItem => Boolean(item) && typeof item === 'object',
      )
    : [];
  const acceptedSuggestedItemIndexes = Array.isArray(
    appointment.data.ocr?.providerMetadata.acceptedSuggestedItemIndexes,
  )
    ? appointment.data.ocr.providerMetadata.acceptedSuggestedItemIndexes.filter(
        (item): item is number => typeof item === 'number',
      )
    : [];
  const hasExistingCalendarEvent = Boolean(
    appointment.data.googleEventId && appointment.data.googleCalendarId,
  );
  const savedCalendarName =
    calendars.data?.find((calendar) => calendar.id === appointment.data.googleCalendarId)?.name ??
    (appointment.data.googleCalendarId === 'primary'
      ? 'ปฏิทินหลัก'
      : appointment.data.googleCalendarId);
  const sourceViewer = sourceIsPdf ? (
    <iframe
      src={sourceUrl}
      title="ไฟล์ PDF ต้นฉบับ"
      className="h-full min-h-[70vh] w-full rounded-2xl bg-slate-100"
    />
  ) : (
    <img
      src={sourceUrl}
      alt="ไฟล์ต้นฉบับ"
      className="max-h-[80vh] w-full rounded-2xl bg-slate-100 object-contain"
    />
  );
  const submit = form.handleSubmit(async (values) => {
    try {
      await api(`/api/appointments/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...values,
          templateData: normalizeTemplateDataForSave(templateData),
          ...(values.templateType === 'meeting' ? { meetingTopicTitle } : {}),
        }),
      });
      if ((appointment.data?.groupItems?.length ?? 0) > 1) {
        await api(`/api/appointments/${id}/group-selection`, {
          method: 'PATCH',
          body: JSON.stringify({
            items: Object.entries(selection).map(([itemId, selected]) => ({
              id: itemId,
              selected,
            })),
          }),
        });
      }
      localStorage.removeItem(draftKey);
      const next = appointment.data?.groupItems?.find(
        (item) =>
          item.id !== id &&
          selection[item.id] &&
          item.status !== 'ready' &&
          item.status !== 'calendar_created',
      );
      if (next) {
        navigate(`/appointments/${next.id}/review`);
        return;
      }
      if (hasExistingCalendarEvent) {
        await api(`/api/appointments/${id}/calendar-event`, {
          method: 'PATCH',
          body: JSON.stringify({
            calendarId: appointment.data.googleCalendarId,
            reminders: [
              { method: 'popup', minutes: 1440 },
              { method: 'popup', minutes: 120 },
            ],
            forceDuplicate: false,
          }),
        });
        navigate(`/appointments/${id}`);
        return;
      }
      navigate(`/appointments/${id}`);
    } catch (error) {
      form.setError('root', {
        message: error instanceof Error ? error.message : 'บันทึกไม่สำเร็จ',
      });
    }
  });
  const suggestionsFor = (key: string) => {
    const current = (fieldSuggestions[key] ?? []).slice(0, 3);
    if (
      key !== 'location' ||
      templateType !== 'meeting' ||
      current.length ||
      appointment.data.location
    )
      return current;
    return (locationHistory.data ?? []).slice(0, 5).map((item) => ({
      value: item.value,
      confidence: 1,
      sourceText: `ใช้ล่าสุด ${new Date(item.lastUsedAt).toLocaleDateString('th-TH')}`,
      origin: 'history' as const,
    }));
  };
  const openPicker = (
    key: string,
    label: string,
    currentValue: string,
    apply: (value: string) => void,
  ) => {
    const suggestions = suggestionsFor(key);
    if (!suggestions.length) return;
    setCustomValue(currentValue);
    setPicker({ key, label, currentValue, suggestions, apply });
  };
  const suggestionHint = (key: string) =>
    suggestionsFor(key).length ? (
      <span className="ml-2 text-xs font-semibold text-teal-700">แตะเพื่อดูตัวเลือก AI</span>
    ) : null;
  const textField = (name: keyof AppointmentPatchInput, label: string, confidenceKey?: string) => {
    const key = confidenceKey ?? String(name);
    const suggestions = suggestionsFor(key);
    return (
      <label className="block">
        <span className="label">
          {label}
          <Confidence value={confidence[key]} />
          {suggestionHint(key)}
        </span>
        <input
          className={`field ${suggestions.length ? 'cursor-pointer bg-teal-50/40' : ''}`}
          {...form.register(name)}
          readOnly={Boolean(suggestions.length)}
          onClick={() =>
            openPicker(key, label, textValue(form.getValues(name)), (value) =>
              form.setValue(name, value),
            )
          }
        />
        <span className="text-xs text-red-600">{form.formState.errors[name]?.message}</span>
      </label>
    );
  };
  const templateText = (key: string, label: string, multiline = false) => (
    <label className={multiline ? 'sm:col-span-2' : ''}>
      <span className="label">
        {label}
        {suggestionHint(key)}
      </span>
      {multiline ? (
        <textarea
          className={`field min-h-20 ${suggestionsFor(key).length ? 'cursor-pointer bg-teal-50/40' : ''}`}
          readOnly={Boolean(suggestionsFor(key).length)}
          value={
            Array.isArray(templateData[key])
              ? (templateData[key] as string[]).join('\n')
              : textValue(templateData[key])
          }
          onClick={() =>
            openPicker(
              key,
              label,
              Array.isArray(templateData[key])
                ? (templateData[key] as string[]).join('\n')
                : textValue(templateData[key]),
              (value) => setTemplateData((current) => ({ ...current, [key]: value })),
            )
          }
          onChange={(event) =>
            setTemplateData((current) => ({ ...current, [key]: event.target.value }))
          }
        />
      ) : (
        <input
          className={`field ${suggestionsFor(key).length ? 'cursor-pointer bg-teal-50/40' : ''}`}
          readOnly={Boolean(suggestionsFor(key).length)}
          value={textValue(templateData[key])}
          onClick={() =>
            openPicker(key, label, textValue(templateData[key]), (value) =>
              setTemplateData((current) => ({ ...current, [key]: value || null })),
            )
          }
          onChange={(event) =>
            setTemplateData((current) => ({ ...current, [key]: event.target.value || null }))
          }
        />
      )}
    </label>
  );
  const templateDate = (key: string, label: string) => (
    <label>
      <span className="label">
        {label}
        {suggestionHint(key)}
      </span>
      <input
        type="date"
        className={`field ${suggestionsFor(key).length ? 'cursor-pointer bg-teal-50/40' : ''}`}
        readOnly={Boolean(suggestionsFor(key).length)}
        value={textValue(templateData[key])}
        onClick={() =>
          openPicker(key, label, textValue(templateData[key]), (value) =>
            setTemplateData((current) => ({ ...current, [key]: value || null })),
          )
        }
        onChange={(event) =>
          setTemplateData((current) => ({ ...current, [key]: event.target.value || null }))
        }
      />
    </label>
  );
  return (
    <Layout title="ตรวจสอบและแก้ไข" wide>
      <form
        className="mx-auto max-w-7xl gap-5 md:grid md:grid-cols-2 md:items-start"
        onSubmit={(event) => void submit(event)}
      >
        <aside className="sticky top-20 hidden min-h-[75vh] rounded-3xl border border-slate-200 bg-white p-3 shadow-sm md:block">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="font-bold">ไฟล์ต้นฉบับ</h2>
            <button
              type="button"
              className="text-sm font-semibold text-teal-700"
              onClick={() => setSourceOpen(true)}
            >
              ขยายเต็มจอ
            </button>
          </div>
          {sourceViewer}
        </aside>
        <div className="space-y-5">
          <button
            type="button"
            className="btn-secondary w-full md:hidden"
            onClick={() => setSourceOpen(true)}
          >
            ดูไฟล์ต้นฉบับ
          </button>
          {(appointment.data.groupItems?.length ?? 0) > 1 && (
            <section className="card space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="font-bold">รายการที่พบในเอกสาร</h2>
                  <p className="text-sm text-slate-600">
                    เลือกเฉพาะรายการที่จะสร้าง และเปิดแก้ไขแต่ละรายการได้
                  </p>
                </div>
                <button
                  type="button"
                  className="text-sm font-semibold text-teal-700"
                  onClick={() =>
                    setSelection(
                      Object.fromEntries(
                        (appointment.data?.groupItems ?? []).map((item) => [item.id, true]),
                      ),
                    )
                  }
                >
                  เลือกทั้งหมด
                </button>
              </div>
              {appointment.data.groupItems?.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 rounded-xl border p-3 ${item.id === id ? 'border-teal-500 bg-teal-50' : 'border-slate-200'}`}
                >
                  <input
                    type="checkbox"
                    className="h-5 w-5 accent-teal-700"
                    checked={selection[item.id] ?? true}
                    onChange={(event) =>
                      setSelection((current) => ({ ...current, [item.id]: event.target.checked }))
                    }
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold">
                      {index + 1}. {item.title ?? 'ยังไม่มีหัวข้อ'}
                    </p>
                    <p className="text-sm text-slate-600">
                      {item.appointmentDate ?? 'ยังไม่พบวันที่'} {item.startTime ?? ''}
                    </p>
                  </div>
                  {item.id !== id && (
                    <Link
                      className="text-sm font-semibold text-teal-700 underline"
                      to={`/appointments/${item.id}/review`}
                    >
                      แก้ไข
                    </Link>
                  )}
                </div>
              ))}
            </section>
          )}
          {suggestedItems.length > 0 && (
            <section className="card space-y-3 border-amber-200 bg-amber-50/50">
              <div>
                <h2 className="font-bold text-amber-950">AI พบรายการอื่นที่อาจแยกได้</h2>
                <p className="text-sm text-amber-900">
                  ยังไม่ได้สร้างเป็นใบนัด เพื่อป้องกันการเพิ่มงานเกินจากไฟล์เดียว
                </p>
              </div>
              {suggestedItems.map((item, index) => {
                const accepted = acceptedSuggestedItemIndexes.includes(index);
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-white p-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-semibold">{item.title || 'AI ไม่พบชื่อเรื่อง'}</p>
                      <p className="text-sm text-slate-600">
                        {item.appointmentDate || 'ไม่พบวันที่'} {item.startTime || ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn-secondary shrink-0"
                      disabled={accepted || createSuggestedItem.isPending}
                      onClick={() => createSuggestedItem.mutate(index)}
                    >
                      {accepted
                        ? 'แยกแล้ว'
                        : createSuggestedItem.isPending
                          ? 'กำลังแยก…'
                          : 'แยกเป็นรายการใหม่'}
                    </button>
                  </div>
                );
              })}
              {createSuggestedItem.error instanceof Error && (
                <ErrorNotice message={createSuggestedItem.error.message} />
              )}
            </section>
          )}
          {appointment.data.ocr?.warnings.map((warning) => (
            <div key={warning} className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
              ⚠ {warning}
            </div>
          ))}
          <section className="card grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2 rounded-xl bg-teal-50 px-3 py-2 text-sm font-semibold text-teal-900">
              ประเภท: {templateLabel}
            </div>
            <div className="sm:col-span-2">
              {textField(
                'title',
                templateType === 'task'
                  ? 'ชื่อภารกิจ *'
                  : templateType === 'meeting'
                    ? 'หัวข้อประชุม *'
                    : templateType === 'activity'
                      ? 'ชื่อกิจกรรม *'
                      : 'หัวข้อการนัดหมาย *',
                'title',
              )}
            </div>
            {templateType === 'meeting' && <div className="sm:col-span-2"><MeetingTopicPicker id={`review-meeting-topic-${id}`} value={meetingTopicTitle} onChange={setMeetingTopicTitle} /></div>}
            <label>
              <span className="label">
                {templateType === 'task'
                  ? 'วันครบกำหนด *'
                  : templateType === 'activity'
                    ? 'วันที่จัดกิจกรรม *'
                    : 'วันที่นัด *'}
                <Confidence value={confidence.appointmentDate} />
                {suggestionHint('appointmentDate')}
              </span>
              <input
                type="date"
                className={`field ${suggestionsFor('appointmentDate').length ? 'cursor-pointer bg-teal-50/40' : ''}`}
                readOnly={Boolean(suggestionsFor('appointmentDate').length)}
                {...form.register('appointmentDate')}
                onClick={() =>
                  openPicker(
                    'appointmentDate',
                    templateType === 'task'
                      ? 'วันครบกำหนด'
                      : templateType === 'activity'
                        ? 'วันที่จัดกิจกรรม'
                        : 'วันที่นัด',
                    form.getValues('appointmentDate'),
                    (value) => form.setValue('appointmentDate', value),
                  )
                }
              />
              <span className="text-xs text-red-600">
                {form.formState.errors.appointmentDate?.message}
              </span>
            </label>
            <label>
              <span className="label">เขตเวลา</span>
              <select className="field" {...form.register('timezone')}>
                <option value="Asia/Bangkok">Asia/Bangkok</option>
              </select>
            </label>
            <label className="sm:col-span-2 flex min-h-11 items-center gap-3 rounded-xl bg-slate-50 px-3">
              <input
                type="checkbox"
                className="h-5 w-5 accent-teal-700"
                checked={allDay}
                onChange={(event) => {
                  if (event.target.checked) {
                    form.setValue('startTime', '');
                    form.setValue('endTime', '');
                  } else {
                    form.setValue('startTime', '08:30');
                    form.setValue('endTime', '16:30');
                  }
                }}
              />
              <span>
                <strong>ทั้งวัน</strong>
                <span className="ml-2 text-sm text-slate-500">ไม่ระบุเวลา</span>
              </span>
            </label>
            {!allDay && (
              <>
                <label>
                  <span className="label">
                    เวลาเริ่ม (24 ชั่วโมง)
                    <Confidence value={confidence.startTime} />
                    {suggestionHint('startTime')}
                  </span>
                  <input
                    type="time"
                    className={`field ${suggestionsFor('startTime').length ? 'cursor-pointer bg-teal-50/40' : ''}`}
                    readOnly={Boolean(suggestionsFor('startTime').length)}
                    {...form.register('startTime')}
                    onClick={() =>
                      openPicker(
                        'startTime',
                        'เวลาเริ่ม',
                        form.getValues('startTime') ?? '',
                        (value) => form.setValue('startTime', value),
                      )
                    }
                  />
                  <span className="text-xs text-red-600">
                    {form.formState.errors.startTime?.message}
                  </span>
                </label>
                <label>
                  <span className="label">วันสิ้นสุด</span>
                  <input type="date" className="field" {...form.register('endDate')} />
                </label>
                <label>
                  <span className="label">
                    เวลาสิ้นสุด (24 ชั่วโมง)
                    <Confidence value={confidence.endTime} />
                    {suggestionHint('endTime')}
                  </span>
                  <input
                    type="time"
                    className={`field ${suggestionsFor('endTime').length ? 'cursor-pointer bg-teal-50/40' : ''}`}
                    readOnly={Boolean(suggestionsFor('endTime').length)}
                    {...form.register('endTime')}
                    onClick={() =>
                      openPicker(
                        'endTime',
                        'เวลาสิ้นสุด',
                        form.getValues('endTime') ?? '',
                        (value) => form.setValue('endTime', value),
                      )
                    }
                  />
                  <span className="text-xs text-red-600">
                    {form.formState.errors.endTime?.message}
                  </span>
                </label>
              </>
            )}
            {templateType !== 'activity' &&
              textField(
                'organization',
                templateType === 'medical'
                  ? 'สถานพยาบาล/หน่วยงาน'
                  : templateType === 'meeting'
                    ? 'หน่วยงาน/ผู้จัดประชุม'
                    : 'หน่วยงาน',
                'organization',
              )}
            {templateType === 'medical' && textField('department', 'แผนก/คลินิก', 'department')}
            {templateType !== 'activity' &&
              textField(
                'doctorName',
                templateType === 'medical'
                  ? 'แพทย์/ผู้ติดต่อ'
                  : templateType === 'meeting'
                    ? 'ผู้ประสานงาน'
                    : 'ผู้มอบหมาย/ผู้ติดต่อ',
                'doctorName',
              )}
            {textField(
              'location',
              templateType === 'medical'
                ? 'อาคาร ชั้น ห้อง/จุดนัด'
                : templateType === 'activity'
                  ? 'สถานที่'
                  : 'สถานที่/ลิงก์',
              'location',
            )}
            {templateType === 'medical' &&
              textField('patientName', 'ชื่อผู้รับบริการ', 'patientName')}
            {textField('referenceNumber', 'เลขที่ใบนัด/อ้างอิง', 'referenceNumber')}
            {textField('phone', 'เบอร์โทรศัพท์', 'phone')}
            {templateType === 'meeting' &&
              templateText('participants', 'ผู้เข้าร่วม (หนึ่งคนต่อบรรทัด)', true)}
            {templateType === 'meeting' &&
              templateText('materials', 'เอกสาร/สิ่งที่ต้องเตรียม (หนึ่งรายการต่อบรรทัด)', true)}
            {templateType === 'meeting' &&
              templateText('relatedTaskTitle', 'งานที่เกี่ยวข้องก่อนประชุม')}
            {templateType === 'meeting' &&
              templateDate('relatedTaskDueDate', 'กำหนดส่งงานที่เกี่ยวข้อง')}
            {templateType === 'task' && (
              <label>
                <span className="label">ระดับความสำคัญ{suggestionHint('priority')}</span>
                <select
                  className="field"
                  value={textValue(templateData.priority, 'normal')}
                  onClick={(event) => {
                    if (suggestionsFor('priority').length) {
                      event.preventDefault();
                      openPicker(
                        'priority',
                        'ระดับความสำคัญ',
                        textValue(templateData.priority, 'normal'),
                        (value) => setTemplateData((current) => ({ ...current, priority: value })),
                      );
                    }
                  }}
                  onChange={(event) =>
                    setTemplateData((current) => ({ ...current, priority: event.target.value }))
                  }
                >
                  <option value="low">ต่ำ</option>
                  <option value="normal">ปกติ</option>
                  <option value="high">สูง/เร่งด่วน</option>
                </select>
              </label>
            )}
            {templateType === 'task' &&
              templateDate('relatedMeetingDate', 'วันที่ประชุมที่เกี่ยวข้อง')}
            {templateType === 'medical' && templateText('serviceType', 'ประเภทบริการ/หัตถการ')}
            {templateType === 'medical' &&
              templateText('preparationItems', 'สิ่งที่ต้องเตรียม (หนึ่งรายการต่อบรรทัด)', true)}
            {templateType === 'activity' && (
              <label>
                <span className="label">ประเภทกิจกรรม</span>
                <input
                  className="field"
                  list="activity-types"
                  value={textValue(templateData.activityType)}
                  onChange={(event) =>
                    setTemplateData((current) => ({
                      ...current,
                      activityType: event.target.value || null,
                    }))
                  }
                  placeholder="เช่น อบรม, สัมมนา, งานพิธี"
                />
                <datalist id="activity-types">
                  {(activityTypes.data ?? []).map((value) => (
                    <option key={value} value={value} />
                  ))}
                </datalist>
                <span className="mt-1 block text-xs text-slate-500">
                  พิมพ์ประเภทใหม่ได้ ระบบจะจำไว้เป็นตัวเลือกครั้งถัดไป
                </span>
              </label>
            )}
            {templateType === 'activity' && templateText('dressCode', 'การแต่งกาย')}
            {templateType === 'activity' && isGolfActivity(templateData.activityType) && (
              <div className="sm:col-span-2 grid gap-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <h3 className="font-bold text-emerald-950">รายละเอียดกิจกรรม Golf</h3>
                  <p className="mt-1 text-xs text-emerald-800">
                    ตรวจสอบข้อมูลที่อ่านจากไฟล์และแก้ไขก่อนบันทึก
                  </p>
                </div>
                {templateText('golfCourse', 'สนามกอล์ฟ')}
                <label>
                  <span className="label">รูปแบบการเริ่ม</span>
                  <select
                    className="field"
                    value={textValue(templateData.golfStartMode, 'tee_time')}
                    onChange={(event) =>
                      setTemplateData((current) => ({
                        ...current,
                        golfStartMode: event.target.value,
                      }))
                    }
                  >
                    <option value="tee_time">Tee time</option>
                    <option value="shotgun">Shotgun start</option>
                  </select>
                </label>
                <label>
                  <span className="label">
                    {textValue(templateData.golfStartMode, 'tee_time') === 'shotgun'
                      ? 'เวลา Shotgun start'
                      : 'Tee time'}
                  </span>
                  <input
                    type="time"
                    className="field"
                    value={textValue(templateData.teeOffTime)}
                    onChange={(event) =>
                      setTemplateData((current) => ({
                        ...current,
                        teeOffTime: event.target.value || null,
                      }))
                    }
                  />
                </label>
                <label>
                  <span className="label">หลุมเริ่มต้น</span>
                  <input
                    className="field"
                    list="review-golf-holes"
                    value={textValue(templateData.golfStartHole)}
                    onChange={(event) =>
                      setTemplateData((current) => ({
                        ...current,
                        golfStartHole: event.target.value || null,
                      }))
                    }
                    placeholder="เช่น 1 หรือ 10A"
                  />
                  <datalist id="review-golf-holes">
                    {Array.from({ length: 18 }, (_, index) => (
                      <option key={index + 1} value={String(index + 1)} />
                    ))}
                  </datalist>
                </label>
                <label>
                  <span className="label">ก๊วน</span>
                  <select
                    className="field"
                    value={textValue(templateData.golfGroupId)}
                    onChange={(event) => {
                      const group = golfGroups.data?.find((item) => item.id === event.target.value);
                      setTemplateData((current) => ({
                        ...current,
                        golfGroupId: group?.id ?? null,
                        golfGroupName: group?.name ?? null,
                        golfers: group?.members ?? [],
                      }));
                    }}
                  >
                    <option value="">เลือกก๊วน</option>
                    {(golfGroups.data ?? []).map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </label>
                {templateText('price', 'ราคา')}
                {templateText('bookedBy', 'ผู้จอง')}
                {templateText('golfers', 'ผู้ร่วมก๊วน (หนึ่งคนต่อบรรทัด)', true)}
              </div>
            )}
            <label className="sm:col-span-2">
              <span className="label">
                {templateType === 'medical' ? 'ข้อแนะนำและสิ่งที่ต้องเตรียม' : 'รายละเอียด'}
                <Confidence value={confidence.instructions} />
                {suggestionHint('instructions')}
              </span>
              <textarea
                className={`field min-h-24 ${suggestionsFor('instructions').length ? 'cursor-pointer bg-teal-50/40' : ''}`}
                readOnly={Boolean(suggestionsFor('instructions').length)}
                {...form.register('instructions')}
                onClick={() =>
                  openPicker(
                    'instructions',
                    templateType === 'medical' ? 'ข้อแนะนำและสิ่งที่ต้องเตรียม' : 'รายละเอียด',
                    form.getValues('instructions') ?? '',
                    (value) => form.setValue('instructions', value),
                  )
                }
              />
            </label>
            <label className="sm:col-span-2">
              <span className="label">
                {templateType === 'task' ? 'ข้อมูลการประชุมที่เกี่ยวข้อง' : 'ข้อความสำคัญอื่น'}
                {suggestionHint('otherNotes')}
              </span>
              <textarea
                className={`field min-h-20 ${suggestionsFor('otherNotes').length ? 'cursor-pointer bg-teal-50/40' : ''}`}
                readOnly={Boolean(suggestionsFor('otherNotes').length)}
                {...form.register('otherNotes')}
                onClick={() =>
                  openPicker(
                    'otherNotes',
                    'ข้อความสำคัญอื่น',
                    form.getValues('otherNotes') ?? '',
                    (value) => form.setValue('otherNotes', value),
                  )
                }
              />
            </label>
          </section>
          {form.formState.errors.root?.message && (
            <ErrorNotice message={form.formState.errors.root.message} />
          )}
          <p className="text-xs text-slate-500">
            ระบบบันทึกฉบับร่างของข้อมูลฟอร์มไว้ในเครื่องนี้ชั่วคราว แต่ไม่เก็บไฟล์เอกสารใน cache
          </p>
          {hasExistingCalendarEvent && (
            <div className="rounded-xl bg-teal-50 px-3 py-2 text-sm text-teal-900">
              บันทึกลง: <strong>{savedCalendarName}</strong> — ระบบจะอัปเดตกิจกรรมเดิมโดยอัตโนมัติ
            </div>
          )}
          <button className="btn-primary w-full" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting
              ? 'กำลังบันทึก…'
              : hasExistingCalendarEvent
                ? 'บันทึกการแก้ไข'
                : 'บันทึกข้อมูล'}
          </button>
        </div>
      </form>
      <section className="mx-auto mt-5 max-w-3xl rounded-2xl border border-slate-200 p-4">
        <h2 className="font-bold">แนบไฟล์เพิ่มเติมในรายการนี้</h2>
        <p className="mt-1 text-sm text-slate-500">
          เพิ่มหลายไฟล์ได้ทันที การอัปโหลดบันทึกแยกจากปุ่มบันทึกข้อมูลด้านบน
        </p>
        <MeetingFilesPanel appointmentId={id} syncToCalendar={hasExistingCalendarEvent} />
      </section>
      {sourceOpen && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-slate-950"
          role="dialog"
          aria-modal="true"
          aria-label="ไฟล์ต้นฉบับ"
        >
          <div className="flex items-center justify-between gap-3 bg-white p-3">
            <strong>ไฟล์ต้นฉบับ</strong>
            <button type="button" className="btn-primary" onClick={() => setSourceOpen(false)}>
              กลับไปกรอกข้อมูล
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-auto p-2">
            {sourceIsPdf ? (
              <iframe
                src={sourceUrl}
                title="ไฟล์ PDF ต้นฉบับแบบเต็มจอ"
                className="h-full min-h-[80vh] w-full bg-white"
              />
            ) : (
              <img
                src={sourceUrl}
                alt="ไฟล์ต้นฉบับแบบเต็มจอ"
                className="mx-auto max-h-full max-w-full object-contain"
              />
            )}
          </div>
        </div>
      )}
      {picker && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-slate-950/45 sm:items-center sm:justify-center"
          role="dialog"
          aria-modal="true"
          aria-label={`เลือกค่า ${picker.label}`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPicker(null);
          }}
        >
          <section className="max-h-[85vh] w-full overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:max-w-lg sm:rounded-3xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold">เลือกค่า: {picker.label}</h2>
                <p className="text-sm text-slate-600">
                  เลือกค่าที่ AI พบในไฟล์ หรือค่าจากประวัติที่ผ่านการตรวจสอบ
                </p>
              </div>
              <button
                type="button"
                className="rounded-full px-3 py-2 text-slate-600"
                onClick={() => setPicker(null)}
                aria-label="ปิด"
              >
                ✕
              </button>
            </div>
            <div className="space-y-3">
              {picker.suggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion.value}-${index}`}
                  type="button"
                  className="w-full rounded-2xl border border-slate-200 p-4 text-left hover:border-teal-500 hover:bg-teal-50"
                  onClick={() => {
                    picker.apply(suggestion.value);
                    setPicker(null);
                  }}
                >
                  <span className="flex items-center justify-between gap-3">
                    <strong className="break-words">{suggestion.value}</strong>
                    {suggestion.origin === 'history' ? (
                      <span className="shrink-0 rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800">
                        จากรายการเดิม
                      </span>
                    ) : (
                      <span
                        className={`shrink-0 rounded-full px-2 py-1 text-xs ${suggestion.confidence < 0.4 ? 'bg-red-100 text-red-800' : suggestion.confidence < 0.65 ? 'bg-amber-100 text-amber-800' : 'bg-teal-100 text-teal-800'}`}
                      >
                        {Math.round(suggestion.confidence * 100)}%
                      </span>
                    )}
                  </span>
                  {suggestion.originalValue && suggestion.originalValue !== suggestion.value && (
                    <span className="mt-2 block text-sm text-amber-800">
                      ต้นฉบับ: {suggestion.originalValue}
                    </span>
                  )}
                  <span className="mt-2 block text-xs text-slate-500">
                    {suggestion.origin === 'history'
                      ? suggestion.sourceText
                      : `อ้างอิง OCR: ${suggestion.sourceText}`}
                  </span>
                </button>
              ))}
            </div>
            <div className="mt-5 border-t border-slate-200 pt-4">
              <label>
                <span className="label">กรอกเอง</span>
                <textarea
                  className="field min-h-20"
                  value={customValue}
                  onChange={(event) => setCustomValue(event.target.value)}
                />
              </label>
              <button
                type="button"
                className="btn-primary mt-3 w-full"
                onClick={() => {
                  picker.apply(customValue);
                  setPicker(null);
                }}
              >
                ใช้ค่าที่กรอกเอง
              </button>
            </div>
          </section>
        </div>
      )}
    </Layout>
  );
}
